import { useState } from "react";
import { toast } from "sonner";

import { SelectorDeFecha } from "@/components/SelectorDeFecha";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { useActualizarPrecioDeIa, useCrearPrecioDeIa } from "@/hooks/useConsumoDeIa";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import type { IaPrecio, IaPrecioInput } from "@/lib/consumoDeIa";

/**
 * Un decimal-string del servidor, leído sin ceros de relleno: `"0.7500"` →
 * `"0.75"`. Solo para MOSTRAR — nunca para el valor que se manda de vuelta,
 * que sale de lo que la persona escribe (ver `aDecimalConPunto`).
 */
function textoInicial(valor: string | null): string {
  if (valor === null || valor.trim() === "") return "";
  const numero = Number(valor);
  return Number.isFinite(numero) ? String(numero) : valor;
}

/**
 * Lo que se teclea → el decimal que el servidor valida.
 *
 * Solo cambia la coma por el punto: NO redondea ni reformatea, porque
 * `numeric` del backend acepta cualquiera de las dos formas y una persona que
 * tipeó "1,50" espera que su cero de más se conserve.
 */
function aDecimalConPunto(texto: string): string {
  return texto.trim().replace(",", ".");
}

interface PrecioDeIaDialogProps {
  /** `null` es ALTA: un período nuevo. Con un `IaPrecio`, es EDICIÓN. */
  precio: IaPrecio | null;
  onCerrar: () => void;
}

/**
 * Alta o edición de un período de precio.
 *
 * EDITAR NUNCA RECALCULA UN COSTO YA REGISTRADO — `ia_consumos.costo_usd`
 * queda congelado al momento de la llamada (`IaPrecioController`, docblock de
 * `UpdateIaPrecioRequest`). Es lo primero que dice el diálogo: sin esa
 * aclaración, alguien podría corregir un precio esperando que reescriba el
 * gasto de septiembre.
 */
export function PrecioDeIaDialog({ precio, onCerrar }: PrecioDeIaDialogProps) {
  const crear = useCrearPrecioDeIa();
  const actualizar = useActualizarPrecioDeIa();
  const guardando = crear.isPending || actualizar.isPending;

  const [proveedor, setProveedor] = useState(precio?.proveedor ?? "gemini");
  const [modelo, setModelo] = useState(precio?.modelo ?? "");
  const [vigenteDesde, setVigenteDesde] = useState(precio?.vigente_desde ?? "");
  const [fechaIncompleta, setFechaIncompleta] = useState(false);
  const [usdEntrada, setUsdEntrada] = useState(textoInicial(precio?.usd_entrada_millon ?? null));
  const [usdSalida, setUsdSalida] = useState(textoInicial(precio?.usd_salida_millon ?? null));
  const [usdCache, setUsdCache] = useState(textoInicial(precio?.usd_cache_millon ?? null));
  const [errores, setErrores] = useState<Record<string, string>>({});

  function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setErrores({});

    if (!proveedor.trim() || !modelo.trim() || !vigenteDesde || fechaIncompleta) {
      setErrores({
        ...( !proveedor.trim() && { proveedor: "El proveedor es obligatorio." }),
        ...(!modelo.trim() && { modelo: "El modelo es obligatorio." }),
        ...((!vigenteDesde || fechaIncompleta) && {
          vigente_desde: "La fecha de vigencia es obligatoria.",
        }),
      });
      return;
    }

    const input: IaPrecioInput = {
      proveedor: proveedor.trim(),
      modelo: modelo.trim(),
      vigente_desde: vigenteDesde,
      usd_entrada_millon: aDecimalConPunto(usdEntrada),
      usd_salida_millon: aDecimalConPunto(usdSalida),
      usd_cache_millon: usdCache.trim() === "" ? null : aDecimalConPunto(usdCache),
    };

    const onSuccess = () => {
      toast.success(precio ? "Precio actualizado." : "Precio agregado al catálogo.");
      onCerrar();
    };
    const onError = (error: unknown) => {
      const delServidor = parseValidationErrors(error);
      if (delServidor) {
        setErrores(delServidor);
        return;
      }
      toast.error(getApiErrorMessage(error, "No pudimos guardar el precio. Inténtalo de nuevo."));
    };

    if (precio) {
      actualizar.mutate({ id: precio.id, input }, { onSuccess, onError });
    } else {
      crear.mutate(input, { onSuccess, onError });
    }
  }

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && !guardando && onCerrar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{precio ? "Editar precio" : "Agregar precio"}</DialogTitle>
          <DialogDescription>
            Los precios son en dólares por cada 1.000.000 de tokens. Guardar este cambio NO
            recalcula el costo de ninguna petición ya registrada: ese costo queda congelado al
            momento en que ocurrió.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={enviar} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="precio-proveedor">Proveedor</Label>
              <Input
                id="precio-proveedor"
                value={proveedor}
                onChange={(evento) => setProveedor(evento.target.value)}
                aria-invalid={Boolean(errores.proveedor)}
                disabled={guardando}
              />
              {errores.proveedor ? (
                <p className="text-destructive text-xs font-medium">{errores.proveedor}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="precio-modelo">Modelo</Label>
              <Input
                id="precio-modelo"
                value={modelo}
                onChange={(evento) => setModelo(evento.target.value)}
                aria-invalid={Boolean(errores.modelo)}
                disabled={guardando}
              />
              {errores.modelo ? (
                <p className="text-destructive text-xs font-medium">{errores.modelo}</p>
              ) : null}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="precio-vigente-desde">Vigente desde</Label>
            <SelectorDeFecha
              id="precio-vigente-desde"
              value={vigenteDesde}
              onChange={setVigenteDesde}
              onTextoIncompleto={setFechaIncompleta}
              disabled={guardando}
              invalido={Boolean(errores.vigente_desde)}
            />
            {errores.vigente_desde ? (
              <p className="text-destructive text-xs font-medium">{errores.vigente_desde}</p>
            ) : null}
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="precio-usd-entrada">USD por millón — entrada</Label>
              <Input
                id="precio-usd-entrada"
                inputMode="decimal"
                value={usdEntrada}
                onChange={(evento) => setUsdEntrada(evento.target.value)}
                aria-invalid={Boolean(errores.usd_entrada_millon)}
                disabled={guardando}
              />
              {errores.usd_entrada_millon ? (
                <p className="text-destructive text-xs font-medium">
                  {errores.usd_entrada_millon}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="precio-usd-salida">USD por millón — salida</Label>
              <Input
                id="precio-usd-salida"
                inputMode="decimal"
                value={usdSalida}
                onChange={(evento) => setUsdSalida(evento.target.value)}
                aria-invalid={Boolean(errores.usd_salida_millon)}
                disabled={guardando}
              />
              {errores.usd_salida_millon ? (
                <p className="text-destructive text-xs font-medium">{errores.usd_salida_millon}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="precio-usd-cache">USD por millón — caché (opcional)</Label>
              <Input
                id="precio-usd-cache"
                inputMode="decimal"
                placeholder="Sin cargo aparte"
                value={usdCache}
                onChange={(evento) => setUsdCache(evento.target.value)}
                aria-invalid={Boolean(errores.usd_cache_millon)}
                disabled={guardando}
              />
              {errores.usd_cache_millon ? (
                <p className="text-destructive text-xs font-medium">{errores.usd_cache_millon}</p>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" disabled={guardando} onClick={onCerrar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={guardando} className="gap-2">
              {guardando ? <Spinner className="text-primary-foreground" /> : null}
              {precio ? "Guardar cambios" : "Agregar precio"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
